import dayjs from 'dayjs';
import React from 'react';
import styled from 'styled-components';

import { withRootTestId } from '@williamhill/rosi-ui-shared/src/lib';
import { formatMoneyWithSymbol, getFormattedReturns } from '@williamhill/rosi-ui-shared/src/lib/money-formatter';
import { IMoney, unitsValueMoney } from '@williamhill/rosi-ui-shared/src/lib/money';
import { Block, robotoFonts, Text } from '@williamhill/rosi-ui-shared/src/components/typography';
import { InjuryProtection as InjuryProtectionIcon } from '@williamhill/rosi-ui-shared/src/assets/icons';
import { PriceOdds } from '../../model';

export type Ticket = {
  placedAt: string;
  potentialReturns: number;
  originalPotentialReturns?: number;
  retailTicketId: string;
  totalStake: number;
  status: string;
  payout: number | null;
  estimatedOdds: PriceOdds | null;
  price: PriceOdds | null;
  originalPrice: PriceOdds | null;
  showVoidedAdjustmentMessage: boolean;
};

export type FooterComponentProps = Ticket & {
  hideOdds?: boolean;
  showInjuryProtectionMessage?: boolean;
};

const withTestId = withRootTestId('bet-tracker-bets-ticket-footer');

export default function FooterComponent({
  placedAt,
  potentialReturns,
  retailTicketId,
  totalStake,
  payout,
  status,
  estimatedOdds,
  price,
  originalPrice,
  originalPotentialReturns,
  hideOdds = false,
  showVoidedAdjustmentMessage,
  showInjuryProtectionMessage = false
}: FooterComponentProps) {
  return (
    <footer>
      <hr />
      {showInjuryProtectionMessage && <InjuryProtectionMessageComponent />}
      {showVoidedAdjustmentMessage && <VoidedAdjustmentMessageComponent />}
      <WagerOddsPayoutWrapper>
        {!hideOdds && <OddsComponent {...{ estimatedOdds, price, originalPrice }} />}
        <WagerComponent totalStake={totalStake} />
        <PayoutComponent
          originalPotentialReturns={originalPotentialReturns}
          payout={unitsValueMoney(payout ?? 0)}
          potentialReturns={unitsValueMoney(potentialReturns)}
          status={status}
        />
      </WagerOddsPayoutWrapper>
      <TicketNumberAndBetPlacedDl>
        <dt>Ticket Number:</dt>
        <dd data-testid={withTestId('ticket-number')}>{retailTicketId}</dd>
        <dt>Bet Placed:</dt>
        <dd data-testid={withTestId('placed-at')}>{dayjs(placedAt).format('MM/DD - h:mma')}</dd>
      </TicketNumberAndBetPlacedDl>
    </footer>
  );
}

function formatMoney(value: number) {
  return formatMoneyWithSymbol(unitsValueMoney(value), '$');
}

const TicketNumberAndBetPlacedDl = styled.dl`
  color: ${({ theme }) => theme.colors.brand.quaternary};
  display: flex;
  font-size: ${robotoFonts.xs};
  margin: 0;

  & > dt:nth-of-type(2) {
    margin-left: auto;
  }

  & > dd {
    margin-inline-start: 0.2rem;
  }
`;

const WagerOddsPayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  > * + * {
    margin-top: 0.3rem;
  }
  margin-bottom: 2rem;
`;

function WagerComponent({ totalStake }: { totalStake: number }) {
  return (
    <FlexRow>
      <Text textAlign="left">Wager</Text>
      <Text data-testid={withTestId('wager')} weight="bold" textAlign="left">
        {formatMoney(totalStake)}
      </Text>
    </FlexRow>
  );
}

function PayoutComponent({
  originalPotentialReturns,
  potentialReturns,
  payout,
  status
}: {
  originalPotentialReturns?: number;
  potentialReturns: IMoney;
  payout: IMoney;
  status: string;
}) {
  return (
    <FlexRow>
      <Text textAlign="left" weight="bold">
        Payout
      </Text>
      <OriginalOddsPayoutBlock>
        {originalPotentialReturns && (
          <OriginalText data-testid={withTestId('original-potential-returns')}>
            {formatMoney(originalPotentialReturns)}
          </OriginalText>
        )}
        <Text data-testid={withTestId('payout')} weight="bold" textAlign="right" as="div">
          {getFormattedReturns({ potentialReturns, payout, status })}
        </Text>
      </OriginalOddsPayoutBlock>
    </FlexRow>
  );
}

function OddsComponent({
  price,
  originalPrice,
  estimatedOdds
}: {
  estimatedOdds: PriceOdds | null;
  price: PriceOdds | null;
  originalPrice: PriceOdds | null;
}) {
  if (price && originalPrice && !areEqualOdds(price, originalPrice)) {
    return (
      <FlexRow>
        <Text textAlign="left">Odds</Text>
        <OriginalOddsPayoutBlock weight="bold" textAlign="right">
          <OriginalText data-testid={withTestId('original-price-odds')}>{originalPrice.a}</OriginalText>
          <OddsText data-testid={withTestId('price-odds')}>{price.a}</OddsText>
        </OriginalOddsPayoutBlock>
      </FlexRow>
    );
  }

  if (price) {
    return (
      <FlexRow>
        <Text textAlign="left">Odds</Text>
        <OriginalOddsPayoutBlock weight="bold" textAlign="right">
          <OddsText data-testid={withTestId('price-odds')}>{price.a}</OddsText>
        </OriginalOddsPayoutBlock>
      </FlexRow>
    );
  }

  if (estimatedOdds) {
    return (
      <FlexRow>
        <Text textAlign="center">Odds</Text>
        <OriginalOddsPayoutBlock weight="bold" textAlign="center" data-testid="">
          {originalPrice && !areEqualOdds(originalPrice, estimatedOdds) && (
            <OriginalText data-testid={withTestId('original-price-odds')}>{originalPrice.a}</OriginalText>
          )}
          <OddsText data-testid={withTestId('estimated-odds')}>{estimatedOdds.a}</OddsText>
        </OriginalOddsPayoutBlock>
      </FlexRow>
    );
  }

  return null;
}

const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

function VoidedAdjustmentMessageComponent() {
  return (
    <VoidedAdjustmentMessageComponentWrapper>
      <Text size="md" textAlign="center">
        One or more of the legs were voided. The odds and payout have been adjusted to reflect this change.
      </Text>
    </VoidedAdjustmentMessageComponentWrapper>
  );
}

function InjuryProtectionMessageComponent() {
  return (
    <InjuryProtectionMessageComponentWrapper>
      <InjuryProtectionIcon style={{ flexShrink: 0 }} />
      <Text size="md" textAlign="center">
        This bet is protected by Injury Protection. The odds and payout have been adjusted to reflect the removal of
        this leg.
      </Text>
    </InjuryProtectionMessageComponentWrapper>
  );
}

const VoidedAdjustmentMessageComponentWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.status.alert};
  padding: 1.2rem 2rem;
  margin-bottom: 2rem;
`;

const InjuryProtectionMessageComponentWrapper = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.status.injuryProtection};
  border-radius: 0.4rem;
  display: flex;
  gap: 0.5rem;
  padding: 1.2rem 2rem;
  margin-bottom: 2rem;
`;

const OriginalOddsPayoutBlock = styled(Block)`
  display: flex;
  justify-content: flex-end;
  > * + * {
    margin-left: 0.5rem;
  }
`;

const OriginalText = styled(Text)`
  text-decoration: line-through ${({ theme }) => theme.colors.red};
  color: ${({ theme }) => theme.colors.neutral};
`;

const OddsText = styled(Text)`
  color: ${({ theme }) => theme.colors.price};
`;

const areEqualOdds = (odds: PriceOdds | null, odds2: PriceOdds | null) => (odds && odds2 ? odds.a === odds2.a : false);
